/**
 * LOHR Build-Your-Ring
 *
 * Custom element that manages the collapsed CTA view + multi-step
 * Build-Your-Ring flow (Timeline -> Setting -> Stone -> Add to Cart).
 *
 * Reads product variant data from an inline JSON script tag and posts
 * directly to /cart/add.js. On success, dispatches CartAddEvent so the
 * existing <cart-drawer-component auto-open> opens automatically.
 *
 * Also exposes window.fireBuildStarted(source) — a defensive, deduped
 * helper that pushes a `build_started` event to GTM's dataLayer (which
 * the merchant wires to a Google Ads Conversion in GTM) and a
 * `BuildStarted` custom event to the Meta Pixel (fbq trackCustom).
 * Both calls are safe whether tags are loaded or not, and consent
 * gating is delegated to Shopify's built-in Customer Privacy wrappers.
 *
 * The fired flag dedupes per page load — a user reopening the builder
 * is not a new "start". Pass a `source` arg ('hero_cta' / 'sticky_bar')
 * so the merchant can split reports later.
 */
(function () {
  if (typeof window === 'undefined') return;
  if (window.fireBuildStarted) return; // already loaded
  let fired = false;

  window.fireBuildStarted = function (source) {
    if (fired) return;
    // Skip inside Shopify theme editor previews so design-mode clicks
    // don't pollute the merchant's conversion data.
    if (window.Shopify && window.Shopify.designMode) return;
    fired = true;

    const eventId = 'bs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 10);
    const byr = document.querySelector('build-your-ring');
    // Use plain `&&` guards, NOT optional chaining (`?.`), on purpose: this file
    // ships untranspiled and `?.` (ES2020) fails to PARSE on iOS Safari < 13.4 —
    // a parse error here discards the whole file, so the <build-your-ring>
    // element never upgrades and the entire builder (price, ATC) goes inert on
    // older iPads. Keep this file ES2017-clean. (byr is an element or null.)
    const currency = (byr && byr.dataset.currencyCode) || 'AUD';
    const productId = (byr && byr.dataset.productId) || 'ring-builder';
    const productTitle = (byr && byr.dataset.productTitle) || 'Custom Ring Builder';

    // GTM / Google Ads — push the GA4-shaped event into dataLayer.
    // The merchant creates a GTM Custom Event trigger on `build_started`
    // and attaches a Google Ads Conversion tag against ID 17975362183.
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'build_started',
        event_source: source || 'unknown',
        event_id: eventId,
        value: 0,
        currency: currency,
        ecommerce: {
          items: [{
            item_id: productId,
            item_name: productTitle,
            item_category: 'engagement_ring',
            item_brand: 'LOHR',
            quantity: 1,
          }],
        },
      });
    } catch (e) {
      // swallow — never throw from tracking
    }

    // Meta Pixel — custom event with value+currency so it's eligible
    // for VO bidding once volume accumulates. content_ids + content_type
    // unlock DPA/Advantage+ retargeting.
    try {
      if (typeof window.fbq === 'function') {
        const metaPayload = {
          content_name: productTitle,
          content_category: 'engagement_ring',
          value: 0.00,
          currency: currency,
          source: source || 'unknown',
        };
        if (productId !== 'ring-builder') {
          metaPayload.content_ids = [String(productId)];
          metaPayload.content_type = 'product_group';
        }
        window.fbq('trackCustom', 'BuildStarted', metaPayload, { eventID: eventId });
      }
    } catch (e) {
      // swallow
    }
  };
})();


class BuildYourRing extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.selectedMetal = null;
    this.selectedSize = null;
    this.selectedStone = null;
    this.selectedHiddenHalo = false;
    this.currentVariant = null;
    this._titleSyncArmed = false; // armed on first user selection -> sync the main title price
    this.completedSteps = new Set();
    // Position of each named option (1-based). Read from data-*-position attrs.
    this.metalPos = 0;
    this.sizePos = 0;
    this.stonePos = 0;
    this.hiddenHaloAvailable = false;
    this.hiddenHaloVariantId = null;
    this.hiddenHaloPrice = 0;
    this.hiddenHaloTitle = 'Round brilliant cut diamond hidden halo';
  }

  connectedCallback() {
    this.metalPos = parseInt(this.dataset.metalPosition || '0', 10);
    this.sizePos = parseInt(this.dataset.sizePosition || '0', 10);
    this.stonePos = parseInt(this.dataset.stonePosition || '0', 10);
    this.hiddenHaloAvailable = this.dataset.hiddenHaloAvailable === 'true';
    this.hiddenHaloVariantId = this.dataset.hiddenHaloVariantId || null;
    this.hiddenHaloPrice = parseInt(this.dataset.hiddenHaloPrice || '0', 10);
    if (isNaN(this.hiddenHaloPrice)) this.hiddenHaloPrice = 0;
    this.hiddenHaloTitle = this.dataset.hiddenHaloTitle || this.hiddenHaloTitle;
    this._readVariants();
    this._readInitialSelection();
    this._bindEvents();
    this._updateAvailability();
    this._refreshSummary();
    this._injectRefinedCTA();
    // The default carat is already selected in Liquid, so the gallery has to
    // match it on load. Without this the shopper sees all five images with
    // 10ct highlighted, and only clicking a carat brings the gallery in line.
    this._swapFeaturedImage();
  }

  /**
   * Refined-for-Life CTA stays as Book-a-Consultation only (no inline
   * BYR). The floating sticky bar carries the BYR call-to-action.
   *
   * Kept as a stub so any old DOM-injected BYR buttons left behind by
   * earlier sessions get cleaned up.
   */
  _injectRefinedCTA() {
    document.querySelectorAll('.byr-refined-build').forEach((el) => el.remove());
  }

  /** Return option1/2/3 for a variant based on the option named `kind`. */
  _variantOptionByKind(v, kind) {
    const pos = kind === 'metal' ? this.metalPos : kind === 'size' ? this.sizePos : kind === 'stone' ? this.stonePos : 0;
    if (pos === 1) return v.option1;
    if (pos === 2) return v.option2;
    if (pos === 3) return v.option3;
    return null;
  }

  /* -------------------- Setup -------------------- */

  _readVariants() {
    const script = this.querySelector('script[data-byr-variants]');
    if (!script) return;
    try {
      this.variants = JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[BuildYourRing] Failed to parse variant JSON', e);
      this.variants = [];
    }
  }

  _readInitialSelection() {
    // Metal and stone are pre-selected to the product's default variant
    // values (set in Liquid). Size is intentionally cleared so the user
    // must actively pick — no default ring size fits everyone.
    const selectedSwatch = this.querySelector('.byr-swatch--selected');
    if (selectedSwatch) {
      this.selectedMetal = selectedSwatch.dataset.byrMetal || null;
    }
    const selectedCarat = this.querySelector('.byr-carat-btn--selected');
    if (selectedCarat) {
      this.selectedStone = selectedCarat.dataset.byrStone || null;
    }
    const sizeSelect = this.querySelector('[data-byr-size-select]');
    if (sizeSelect) {
      sizeSelect.value = '';
      this.selectedSize = null;
    }
    this._recomputeVariant();
  }

  _ordinal(n) {
    const v = n % 100;
    if (v >= 11 && v <= 13) return 'th';
    switch (n % 10) {
      case 1: return 'st';
      case 2: return 'nd';
      case 3: return 'rd';
      default: return 'th';
    }
  }

  /* -------------------- Event binding -------------------- */

  _bindEvents() {
    this.addEventListener('click', (e) => {
      const actionEl = e.target.closest('[data-byr-action]');
      if (actionEl) {
        e.preventDefault();
        const action = actionEl.dataset.byrAction;
        if (action === 'open-flow') return this._openFlow();
        if (action === 'simple-atc') return this._handleAtc(e);
        if (action === 'back') return this._goBack();
        if (action === 'next') return this._goNext(actionEl);
        if (action === 'goto-step') return this._gotoStep(parseInt(actionEl.dataset.byrTargetStep || '1', 10));
        if (action === 'open-hint') return this._openHintModal();
        if (action === 'open-enquiry') return this._openProductEnquiry();
        if (action === 'open-size-guide') return this._openSizeGuide();
      }

      const swatch = e.target.closest('.byr-swatch');
      if (swatch) {
        e.preventDefault();
        this._selectMetal(swatch.dataset.byrMetal);
      }

      const carat = e.target.closest('.byr-carat-btn');
      if (carat) {
        e.preventDefault();
        this._selectStone(carat.dataset.byrStone);
      }

      const halo = e.target.closest('.byr-halo-btn');
      if (halo) {
        e.preventDefault();
        this._selectHiddenHalo(halo.dataset.byrHalo === 'yes');
      }
    });

    const sizeSelect = this.querySelector('[data-byr-size-select]');
    if (sizeSelect) {
      sizeSelect.addEventListener('change', (e) => this._selectSize(e.target.value));
    }

    const atcForm = this.querySelector('[data-byr-atc-form]');
    if (atcForm) {
      atcForm.addEventListener('submit', (e) => this._handleAtc(e));
    }
  }

  /* -------------------- View / step navigation -------------------- */

  _openFlow() {
    // Fire BuildStarted tracking for Google Ads + Meta. Deduped per page
    // load via the fired flag inside fireBuildStarted, so reopening the
    // builder is not double-counted.
    if (typeof window.fireBuildStarted === 'function') {
      window.fireBuildStarted('hero_cta');
    }

    // Measure the BYR's current viewport offset so we can keep the user
    // anchored after the CSS hides all the surrounding blocks (otherwise
    // the BYR shifts up the page and the user sees an empty area).
    const beforeTop = this.getBoundingClientRect().top;

    this.dataset.currentView = 'flow';
    this._showView('flow');
    this._setStep(1);
    document.body.classList.add('byr-flow-active');

    // After hiding sibling blocks, compensate the page scroll so the BYR
    // stays in the same place in the viewport (no jump).
    requestAnimationFrame(() => {
      const afterTop = this.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;
      if (Math.abs(delta) > 1) {
        window.scrollBy({ top: delta, behavior: 'instant' });
      }
    });
  }

  _goBack() {
    const step = parseInt(this.dataset.currentStep || '1', 10);
    if (step > 1) {
      this._setStep(step - 1);
      return;
    }
    // Step 1 -> collapsed view. Same anchoring trick as _openFlow: preserve
    // viewport position when the sibling blocks come back into the layout.
    const beforeTop = this.getBoundingClientRect().top;
    this.dataset.currentView = 'collapsed';
    this._showView('collapsed');
    document.body.classList.remove('byr-flow-active');
    requestAnimationFrame(() => {
      const afterTop = this.getBoundingClientRect().top;
      const delta = afterTop - beforeTop;
      if (Math.abs(delta) > 1) {
        window.scrollBy({ top: delta, behavior: 'instant' });
      }
    });
  }

  _goNext(triggerEl) {
    const nextStep = parseInt(triggerEl.dataset.nextStep || '0', 10);
    if (!nextStep) return;
    const currentStep = parseInt(this.dataset.currentStep || '1', 10);

    if (currentStep === 2 && !this.selectedSize) {
      this._showError('Please select a ring size.');
      return;
    }

    this.completedSteps.add(currentStep);
    this._setStep(nextStep);
  }

  /**
   * Jump directly to a step via the progress indicator. Allow going back
   * to any previously visited step freely. Allow going forward only to
   * a step the user has already reached at least once.
   */
  _gotoStep(target) {
    if (!target || target < 1 || target > 3) return;
    const current = parseInt(this.dataset.currentStep || '1', 10);
    if (target === current) return;

    if (target < current) {
      // Going back is always allowed
      this._setStep(target);
      return;
    }

    // Going forward: only allow if user has already completed the
    // current step (i.e. they're returning to a step they once moved
    // past). If the in-between gate fails, surface a friendly hint.
    if (current === 2 && !this.selectedSize) {
      this._showError('Please select a ring size to continue.');
      return;
    }
    this.completedSteps.add(current);
    this._setStep(target);
  }

  _showView(view) {
    this.querySelectorAll('.byr-view').forEach((el) => {
      const isMatch = el.dataset.byrView === view;
      el.hidden = !isMatch;
    });
    // Scrolling is handled by _openFlow / _goBack to preserve the viewport
    // anchor without a visible jump.
  }

  _setStep(step) {
    this.dataset.currentStep = String(step);

    this.querySelectorAll('.byr-step').forEach((el) => {
      el.hidden = parseInt(el.dataset.byrStep, 10) !== step;
    });

    this.querySelectorAll('[data-byr-step-indicator]').forEach((el) => {
      const idx = parseInt(el.dataset.byrStepIndicator, 10);
      if (idx === step) {
        el.dataset.active = 'true';
        el.setAttribute('aria-current', 'step');
        el.removeAttribute('data-completed');
      } else if (this.completedSteps.has(idx) || idx < step) {
        el.dataset.completed = 'true';
        el.removeAttribute('data-active');
        el.removeAttribute('aria-current');
      } else {
        el.removeAttribute('data-active');
        el.removeAttribute('data-completed');
        el.removeAttribute('aria-current');
      }
    });

    // Move focus to the new step's heading so screen-reader users hear
    // the page state change.
    const activeStepEl = this.querySelector(`[data-byr-step="${step}"]:not([hidden])`);
    if (activeStepEl) {
      const heading = activeStepEl.querySelector('.byr-step-heading');
      if (heading) {
        heading.setAttribute('tabindex', '-1');
        try {
          heading.focus({ preventScroll: true });
        } catch (e) {
          // ignore if focus fails
        }
      }
    }

    this._clearError();
    this._refreshSummary();
  }

  /* -------------------- Variant selection -------------------- */

  _selectMetal(metal) {
    if (!metal) return;
    this.selectedMetal = metal;
    this._titleSyncArmed = true;

    // Sync all metal swatches in both collapsed and expanded views
    this.querySelectorAll('.byr-swatch').forEach((sw) => {
      const isMatch = sw.dataset.byrMetal === metal;
      sw.classList.toggle('byr-swatch--selected', isMatch);
      sw.setAttribute('aria-pressed', String(isMatch));
    });

    // Update visible metal label
    this.querySelectorAll('[data-byr-metal-label]').forEach((el) => {
      el.textContent = metal;
    });

    this._recomputeVariant();
    this._swapFeaturedImage();
    this._updateAvailability();
    this._refreshSummary();
  }

  _selectSize(size) {
    this.selectedSize = size || null;
    this._titleSyncArmed = true;
    this._recomputeVariant();
    this._swapFeaturedImage();
    this._updateAvailability();
    this._refreshSummary();
    if (size) this._clearError();
  }

  _selectStone(stone) {
    if (!stone) return;
    this.selectedStone = stone;
    this._titleSyncArmed = true;
    this.querySelectorAll('.byr-carat-btn').forEach((btn) => {
      const isMatch = btn.dataset.byrStone === stone;
      btn.classList.toggle('byr-carat-btn--selected', isMatch);
      btn.setAttribute('aria-pressed', String(isMatch));
    });
    this._recomputeVariant();
    this._swapFeaturedImage();
    this._updateAvailability();
    this._refreshSummary();
    this._clearError();
  }

  _selectHiddenHalo(selected) {
    this.selectedHiddenHalo = !!selected;
    this._titleSyncArmed = true;
    this.querySelectorAll('.byr-halo-btn').forEach((btn) => {
      const isMatch = (btn.dataset.byrHalo === 'yes') === this.selectedHiddenHalo;
      btn.classList.toggle('byr-halo-btn--selected', isMatch);
      btn.setAttribute('aria-pressed', String(isMatch));
    });
    this._updatePriceDisplay();
    this._updateAvailability();
    this._refreshSummary();
    this._clearError();
  }

  _recomputeVariant() {
    if (!this.variants.length) {
      this.currentVariant = null;
      return;
    }
    const matches = this.variants.filter((v) => {
      if (this.selectedMetal && this.metalPos && this._variantOptionByKind(v, 'metal') !== this.selectedMetal) return false;
      if (this.selectedSize && this.sizePos && this._variantOptionByKind(v, 'size') !== this.selectedSize) return false;
      if (this.selectedStone && this.stonePos && this._variantOptionByKind(v, 'stone') !== this.selectedStone) return false;
      return true;
    });
    // Prefer available + exact match, else first match, else null
    this.currentVariant =
      matches.find((v) => v.available) || matches[0] || null;

    // Update hidden variant ID input on ATC form
    const input = this.querySelector('[data-byr-variant-id]');
    if (input && this.currentVariant) {
      input.value = String(this.currentVariant.id);
    }

    this._updatePriceDisplay();
  }

  _updatePriceDisplay() {
    if (!this.currentVariant) return;
    const currency = this.dataset.currencyCode || '';
    const addOnPrice = this.selectedHiddenHalo ? this.hiddenHaloPrice : 0;
    const formatted = this._formatMoney(this.currentVariant.price + addOnPrice, currency);
    this.querySelectorAll('[data-byr-price], [data-byr-atc-price]').forEach((el) => {
      el.textContent = `${formatted} ${currency}`;
    });
    // Molly's request: once a carat (or metal) is picked up front, the LARGE price
    // under the product name reflects it. <product-price> is a sibling in the same
    // section (not inside this element). Gated so the server-rendered "From $X"
    // stays until the first real selection. ES2017-clean (no ?.) -- ships untranspiled.
    if (this._titleSyncArmed) {
      const section = this.closest('.shopify-section');
      const titlePrices = section ? section.querySelectorAll('product-price .price') : [];
      titlePrices.forEach((el) => {
        el.textContent = formatted;
      });
    }
  }

  _updateAvailability() {
    const atcBtn = this.querySelector('[data-byr-add-to-cart]');
    if (!atcBtn) return;

    // Required selections: metal + size always; stone only when the product
    // has a stone-size option.
    const metalOk = !this.metalPos || !!this.selectedMetal;
    const sizeOk = !this.sizePos || !!this.selectedSize;
    const stoneOk = !this.stonePos || !!this.selectedStone;
    const haloOk = !this.selectedHiddenHalo || (this.hiddenHaloAvailable && !!this.hiddenHaloVariantId);
    const ready = metalOk && sizeOk && stoneOk && haloOk && this.currentVariant && this.currentVariant.available;
    if (ready) {
      atcBtn.removeAttribute('disabled');
      atcBtn.setAttribute('aria-disabled', 'false');
    } else {
      atcBtn.setAttribute('disabled', 'disabled');
      atcBtn.setAttribute('aria-disabled', 'true');
    }
  }

  _refreshSummary() {
    // Step 1 meta: dispatch date (always show — it's set, not chosen)
    const step1Meta = this.querySelector('[data-byr-progress-meta="1"]');
    const dispatchEl = this.querySelector('[data-byr-dispatch-date]');
    if (step1Meta && dispatchEl) {
      step1Meta.textContent = dispatchEl.textContent;
    }

    // Step 2 meta: only show metal + size summary AFTER user has visited step 2
    // (i.e., once they've actively engaged with it). Avoid pre-populating
    // defaults which makes it look completed when it's not.
    const step2Meta = this.querySelector('[data-byr-progress-meta="2"]');
    if (step2Meta) {
      const visited = this.completedSteps.has(2) || parseInt(this.dataset.currentStep || '1', 10) >= 2;
      if (visited && this.selectedSize) {
        const parts = [];
        if (this.selectedMetal) parts.push(this.selectedMetal);
        if (this.selectedSize) parts.push(`Size ${this.selectedSize}`);
        if (this.selectedHiddenHalo) parts.push('Hidden halo');
        step2Meta.textContent = parts.join(' · ');
      } else {
        step2Meta.textContent = '';
      }
    }
  }

  /* -------------------- Image swap -------------------- */

  /**
   * Move the gallery to the slide that already holds this variant's photo.
   *
   * The previous version overwrote the FIRST slide's <img> src with the
   * variant's image URL. That left the gallery inconsistent: the large image
   * showed the 20ct photo while thumbnail 1 still showed the original, because
   * thumbnails are separate <img> elements it never touched. It also duplicated
   * a photo that was already present further along the strip.
   *
   * Navigating instead means the swiper keeps slides and thumbnails in sync by
   * itself, nothing is mutated, and nothing can go stale.
   *
   * Opt-in per product (data-gallery-sync). Most products have no image assigned
   * per option, so doing this everywhere would jump to the wrong photo.
   */
  /**
   * Show only the photography for the chosen stone size.
   *
   * Images are grouped server-side by stone size (see data-media-map on the
   * block) using the product's real option values, so a metal karat like
   * "18ct white gold" is never mistaken for a stone size. A size with no
   * photography of its own falls back to the general images rather than an
   * empty gallery.
   *
   * Opt-in per product via data-gallery-sync. Most products have no image
   * tagged per option, so filtering everywhere would empty their galleries.
   */
  /**
   * Metal colour for the gallery filter (TY-098).
   *
   * "9ct Yellow Gold" and "18ct Yellow Gold" are the same photograph, so the
   * karat is dropped and only the colour is keyed. Platinum has no renders of
   * its own anywhere in the catalogue and is the same white metal, so it reads
   * the white-gold set. A bi-metal value ("Platinum or White Gold on Yellow
   * Gold") keys on its full lowercased name, which is how the Liquid grouped it.
   *
   * Mirrors the normalisation in snippets/byr-metal-swatch-fill.liquid, which
   * already colours the swatches from these same substrings.
   */
  _metalMediaKey() {
    if (!this.selectedMetal) return null;
    const name = String(this.selectedMetal).toLowerCase();
    if (name.indexOf(' on ') !== -1) return name;
    if (name.indexOf('yellow') !== -1) return 'yellow';
    if (name.indexOf('rose') !== -1) return 'rose';
    if (name.indexOf('white') !== -1) return 'white';
    if (name.indexOf('platinum') !== -1) return 'white';
    return null;
  }

  /**
   * Move the gallery to the renders for the chosen metal (TY-098).
   *
   * Runs only where the product has metal-tagged images, so the 20 rings with
   * no per-metal alt text keep today's behaviour instead of showing an empty
   * gallery. A colour with no renders of its own also falls through to the
   * general set rather than emptying the rail.
   */
  _swapMetalImage() {
    if (this.dataset.metalGallerySync !== 'true') return false;

    const container = document.querySelector('.product-information__media');
    if (!container) return false;
    const swiper = container.querySelector('product-media-swiper');
    if (!swiper) return false;
    // On first load this can run before <product-media-swiper> is upgraded, so
    // its method does not exist yet. Wait for the definition instead of
    // dropping the call, or the load-time filter is silently a no-op whenever
    // that element upgrades first. Same guard as the stone-size path below.
    if (typeof swiper.filterByMediaIds !== 'function') {
      if (!this._awaitingMetalSwiper) {
        this._awaitingMetalSwiper = true;
        customElements.whenDefined('product-media-swiper').then(() => {
          this._awaitingMetalSwiper = false;
          this._swapFeaturedImage();
        });
      }
      return true;
    }

    let map = null;
    try {
      map = JSON.parse(this.dataset.metalMediaMap || 'null');
    } catch (e) {
      map = null;
    }
    if (!map) return false;

    const key = this._metalMediaKey();
    if (!key) {
      swiper.filterByMediaIds(null);
      return true;
    }

    const general = map.__general__ || [];
    let metal = map[key] || [];

    // A bi-metal value with no dedicated two-tone render falls back to the
    // renders of the two colours it names, which is closer to the real piece
    // than the default gallery. Dauphine, for instance, has six white-gold
    // shots and one each of yellow and rose but no two-tone photography, so
    // "Platinum or White Gold on Yellow Gold" shows the white AND yellow
    // renders rather than opening on white alone.
    if (!metal.length && key.indexOf(' on ') !== -1) {
      const parts = key.split(' on ');
      const seen = {};
      const merged = [];
      ['yellow', 'rose', 'white', 'platinum'].forEach(function (colour) {
        const named = parts.some(function (part) { return part.indexOf(colour) !== -1; });
        if (!named) return;
        // platinum has no renders of its own; it is the white metal.
        const group = map[colour === 'platinum' ? 'white' : colour] || [];
        group.forEach(function (id) {
          if (!seen[id]) { seen[id] = true; merged.push(id); }
        });
      });
      metal = merged;
    }

    // Still nothing to show -> leave the gallery as it is rather than
    // emptying the rail.
    if (!metal.length) {
      swiper.filterByMediaIds(null);
      return true;
    }
    // The metal's own renders lead; the general shots (on-body, detail) follow,
    // matching how the stone-size filter keeps the selling images on the page.
    swiper.filterByMediaIds(metal.concat(general));
    return true;
  }

  _swapFeaturedImage() {
    // Metal sync takes precedence: it is the one the ticket asks for and the
    // two filters write to the same rail, so running both would fight.
    if (this._swapMetalImage()) return;
    if (this.dataset.gallerySync !== 'true') return;

    const container = document.querySelector('.product-information__media');
    if (!container) return;
    const swiper = container.querySelector('product-media-swiper');
    if (!swiper) return;
    // On first load this can run before <product-media-swiper> has been
    // upgraded, so its method would not exist yet. Wait for the definition
    // rather than dropping the call, otherwise the load-time filter is
    // silently a no-op whenever this element upgrades first.
    if (typeof swiper.filterByMediaIds !== 'function') {
      if (this._awaitingSwiper) return;
      this._awaitingSwiper = true;
      customElements.whenDefined('product-media-swiper').then(() => {
        this._awaitingSwiper = false;
        this._swapFeaturedImage();
      });
      return;
    }

    var map = null;
    try {
      map = JSON.parse(this.dataset.mediaMap || 'null');
    } catch (e) {
      map = null;
    }
    if (!map) return;

    // Show everything again when no stone size is chosen yet.
    if (!this.selectedStone) {
      swiper.filterByMediaIds(null);
      return;
    }

    // A stone size shows the general photography AND its own renders, with the
    // general shots first. Those on-body images are the ones that sell the
    // piece, and hiding them behind a size selection removed them from the page
    // entirely, because every size had renders of its own. A size with no
    // renders of its own simply falls back to the general set.
    var general = map.__general__ || [];
    var sized = map[this.selectedStone] || [];
    var ids = sized.length ? general.concat(sized) : general;
    swiper.filterByMediaIds(ids.length ? ids : null);
  }

  /* -------------------- ATC -------------------- */

  async _handleAtc(e) {
    e.preventDefault();
    if (!this.currentVariant) {
      this._showError('Please complete your selection.');
      return;
    }
    // Only require metal+size for ring products. Non-rings (e.g. studs)
    // have sizePos == 0 and just need a valid current variant.
    const needsMetal = this.metalPos > 0;
    const needsSize = this.sizePos > 0;
    const needsStone = this.stonePos > 0;
    if (needsMetal && !this.selectedMetal) {
      this._showError('Please choose a metal.');
      return;
    }
    if (needsSize && !this.selectedSize) {
      this._showError('Please choose a ring size.');
      return;
    }
    if (needsStone && !this.selectedStone) {
      this._showError('Please choose a stone size.');
      return;
    }
    if (!this.currentVariant.available) {
      this._showError(needsSize
        ? 'This combination is not currently available. Please choose a different size.'
        : 'This item is not currently available.');
      return;
    }
    if (this.selectedHiddenHalo && (!this.hiddenHaloAvailable || !this.hiddenHaloVariantId)) {
      this._showError('Hidden halo customisation is not available for this ring right now. Please contact us to complete your order.');
      return;
    }

    const atcBtn = this.querySelector('[data-byr-add-to-cart]');
    const labelEl = atcBtn ? atcBtn.querySelector('.byr-atc-label') : null;
    const originalLabel = labelEl ? labelEl.textContent : null;
    if (atcBtn) {
      atcBtn.setAttribute('disabled', 'disabled');
      if (labelEl) labelEl.textContent = 'Adding...';
    }

    try {
      const ringItem = { id: this.currentVariant.id, quantity: 1 };
      if (this.selectedHiddenHalo) {
        ringItem.properties = {
          'Hidden Halo': this.hiddenHaloTitle,
        };
      }

      let items = [ringItem];
      if (this.selectedHiddenHalo) {
        const haloVariantId = parseInt(this.hiddenHaloVariantId, 10);
        const haloItem = {
          id: isNaN(haloVariantId) ? this.hiddenHaloVariantId : haloVariantId,
          quantity: 1,
          properties: {
            'For': this.dataset.productTitle || 'Custom ring',
            '_ring_variant_id': String(this.currentVariant.id),
          },
        };
        // Shopify renders the newest added line first in the cart drawer, so
        // send the add-on before the ring to keep the ring visually primary.
        items = [haloItem, ringItem];
      }

      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: items,
        }),
      });

      if (!res.ok) {
        // Shopify sends a JSON body with a human-readable `description`
        // (e.g. "out of stock", "you can only add N"). Guarded parse, then
        // show that friendly message directly — never let a missing/odd
        // body turn into a cryptic error.
        const errData = await res.json().catch(() => ({}));
        this._showError(
          errData.description ||
            errData.message ||
            'This item could not be added to your bag right now. Please try again, or contact us to complete your order.'
        );
        if (atcBtn) atcBtn.removeAttribute('disabled');
        if (labelEl && originalLabel) labelEl.textContent = originalLabel;
        return;
      }

      // A 2xx means the item was added to the cart server-side. The body is
      // normally the added line item as JSON — but in privacy-restricted
      // contexts (e.g. iOS Safari Private Browsing on a cross-site preview
      // domain, where the cart/session cookies are partitioned by ITP) the
      // cart endpoint can return a 2xx with an empty or non-JSON body. In
      // WebKit, Response.json() then throws a cryptic SyntaxError ("The
      // string did not match the expected pattern.") which must never reach
      // a customer. The add already succeeded, so guard the parse, fall back
      // to null, and still open the cart.
      const data = await res.json().catch(() => null);

      // Dispatch the same event the existing add-to-cart component fires
      // so cart-drawer-component auto-opens.
      const cartUpdate = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: data,
          sourceId: this.currentVariant.id,
          data: {
            source: 'build-your-ring',
            itemCount: items.length,
            variantId: this.currentVariant.id,
            productId: this.dataset.productId,
          },
        },
      });
      this.dispatchEvent(cartUpdate);

      // Open the cart drawer. The existing theme's add-to-cart-component
      // uses showDialog(); call it directly so the drawer opens reliably
      // even when the cart drawer doesn't have the auto-open attribute.
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer) {
        if (typeof drawer.showDialog === 'function') {
          drawer.showDialog();
        } else if (typeof drawer.open === 'function') {
          drawer.open();
        }
      }

      if (labelEl) labelEl.textContent = 'Added';
      setTimeout(() => {
        if (labelEl && originalLabel) labelEl.textContent = originalLabel;
        if (atcBtn) atcBtn.removeAttribute('disabled');
      }, 2000);
    } catch (err) {
      // Unexpected failure (network drop, etc.). Log the real error for us,
      // but never surface a raw engine message (e.g. a WebKit "The string
      // did not match the expected pattern." SyntaxError) to the customer.
      console.warn('[BuildYourRing] ATC failed:', err);
      this._showError('Something went wrong adding this to your bag. Please try again, or contact us to complete your order.');
      if (atcBtn) atcBtn.removeAttribute('disabled');
      if (labelEl && originalLabel) labelEl.textContent = originalLabel;
    }
  }

  /* -------------------- Errors -------------------- */

  _showError(message) {
    const errEl = this.querySelector('[data-byr-error]');
    if (!errEl) return;
    errEl.textContent = message;
    errEl.hidden = false;
  }

  _clearError() {
    const errEl = this.querySelector('[data-byr-error]');
    if (!errEl) return;
    errEl.textContent = '';
    errEl.hidden = true;
  }

  /* -------------------- Tertiary action handlers -------------------- */

  _openHintModal() {
    // Open the shared Klaviyo "Drop a Hint" gift form. It's rendered on every
    // product page that has the builder (snippets/drop-a-hint-modal.liquid via
    // the BYR block) and listens for this event.
    if (document.querySelector('[data-drop-a-hint-modal]')) {
      document.dispatchEvent(new CustomEvent('lohr:open-drop-a-hint'));
      return;
    }
    // Legacy fallbacks: an older Drop-a-Hint block instance, else a mailto so
    // the link is never dead.
    const trigger = document.querySelector('[data-drop-a-hint-trigger], .drop-a-hint-button, button[aria-label*="hint" i]');
    if (trigger) {
      trigger.click();
      return;
    }
    const title = this.dataset.productTitle || 'a LOHR piece';
    const url = window.location.href;
    const subject = encodeURIComponent(`A piece I thought you'd love: ${title}`);
    const body = encodeURIComponent(`I came across this LOHR piece and thought of you:\n\n${url}\n\n`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  }

  _openProductEnquiry() {
    const params = new URLSearchParams();
    const productUrl = new URL(this.dataset.productUrl || window.location.pathname, window.location.origin);
    const displayedPrice = this.querySelector('[data-byr-price], [data-byr-atc-price]');
    const hasCompleteSelection =
      (!this.metalPos || !!this.selectedMetal) &&
      (!this.sizePos || !!this.selectedSize) &&
      (!this.stonePos || !!this.selectedStone);

    params.set('enquire_piece', this.dataset.productTitle || 'LOHR piece');
    params.set('enquire_url', productUrl.href);
    if (displayedPrice && displayedPrice.textContent.trim()) {
      params.set('enquire_price', displayedPrice.textContent.trim());
    }
    if (this.selectedMetal) params.set('enquire_metal', this.selectedMetal);
    if (this.selectedStone) params.set('enquire_stone', this.selectedStone);
    if (this.selectedSize) params.set('enquire_size', this.selectedSize);
    if (this.selectedHiddenHalo) params.set('enquire_hidden_halo', this.hiddenHaloTitle || 'Yes');
    if (hasCompleteSelection && this.currentVariant) {
      params.set('enquire_variant_id', String(this.currentVariant.id));
    }

    window.location.href = `/pages/contact?${params.toString()}`;
  }

  _openSizeGuide() {
    const modal = this.querySelector('[data-byr-size-modal]');
    if (!modal) return;

    // Wire close, backdrop-click and tab switching once.
    if (!modal._byrWired) {
      modal.addEventListener('click', (e) => {
        // Clicks on the dialog element itself (the backdrop area) or the
        // close button dismiss the modal; clicks inside .__inner do not.
        if (e.target === modal || e.target.closest('[data-byr-size-close]')) {
          this._closeSizeGuide();
        }
      });
      modal.querySelectorAll('[data-byr-size-tab]').forEach((tab) => {
        tab.addEventListener('click', () => this._selectSizeTab(modal, tab.dataset.byrSizeTab));
      });
      modal._byrWired = true;
    }

    // Native <dialog> renders in the top layer (escapes the section's
    // stacking context) and gives focus-trap + Escape handling for free.
    if (typeof modal.showModal === 'function') {
      if (!modal.open) modal.showModal();
    } else {
      modal.setAttribute('open', '');
    }
  }

  _closeSizeGuide() {
    const modal = this.querySelector('[data-byr-size-modal]');
    if (!modal) return;
    if (typeof modal.close === 'function') {
      modal.close();
    } else {
      modal.removeAttribute('open');
    }
  }

  _selectSizeTab(modal, name) {
    modal.querySelectorAll('[data-byr-size-tab]').forEach((tab) => {
      const on = tab.dataset.byrSizeTab === name;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    modal.querySelectorAll('[data-byr-size-panel]').forEach((panel) => {
      panel.hidden = panel.dataset.byrSizePanel !== name;
    });
  }

  /* -------------------- Money formatting -------------------- */

  _formatMoney(cents, currency) {
    const amount = (cents || 0) / 100;
    if (amount === Math.round(amount)) {
      return `$${amount.toLocaleString('en-AU')}`;
    }
    return `$${amount.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
}

if (!customElements.get('build-your-ring')) {
  customElements.define('build-your-ring', BuildYourRing);
}
